import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Heart, Users, Shield, Building, MessageCircle } from "lucide-react";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import RelatedResources from "../components/RelatedResources";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20with%20Health%20Insurance%20Advisory%20in%20Coimbatore";

const FAQS = [
  { q: "How much can I save in tax under Section 80D?", a: "Premiums for self, spouse, and children: up to ₹25,000 (₹50,000 if any insured is a senior citizen). Premiums for parents: additional ₹25,000 (₹50,000 if parents are senior citizens). Maximum total deduction can reach ₹1,00,000 per year. Section 80D also covers ₹5,000 for preventive health check-ups within these limits." },
  { q: "What is the right health insurance cover amount?", a: "For metro cities like Coimbatore, a minimum cover of ₹5–10 lakh per individual is recommended given current hospitalization costs. Family floater plans of ₹10–15 lakh work well for a family of 4. Add a top-up policy beyond ₹15 lakh for cost-effective higher coverage. We help you choose based on your age, family size, and city." },
  { q: "What is the difference between an Individual and Family Floater plan?", a: "Individual plans cover one person with the full sum insured available. Family Floater plans cover the entire family under a shared sum insured — economical and convenient. However, if multiple family members have major claims in the same year, the cover may run out. We recommend a hybrid: floater + a separate plan for senior parents." },
  { q: "Are pre-existing diseases covered immediately?", a: "Most policies impose a waiting period of 2–4 years for pre-existing conditions like diabetes, hypertension, or thyroid issues. Some IRDAI-approved insurers now offer policies with reduced 1-year or even 90-day waiting periods at slightly higher premiums. Disclose all conditions during proposal to avoid claim rejection." },
  { q: "Why should an MSME offer Group Health Insurance?", a: "Group plans cost 30–50% less than individual covers, offer instant coverage for all employees with no waiting period, and the entire premium is a tax-deductible business expense. They are a strong tool for talent retention, especially in competitive sectors. Even a 5-employee firm can buy a group policy starting from ₹15,000–₹25,000/year." },
  { q: "How does cashless treatment work?", a: "When admitted to a network hospital, the hospital coordinates directly with the insurer for pre-authorisation. You only pay for non-medical expenses (admission kit, telephone, etc.) and any sub-limited deductions. We help you choose plans with the widest network in Coimbatore including KMCH, GKNM, PSG, KG Hospital, and Sri Ramakrishna Hospital." },
  { q: "Can I claim tax benefit on health check-up expenses?", a: "Yes. Up to ₹5,000 spent on preventive health check-ups for self, spouse, children, and parents qualifies for deduction under Section 80D — within the overall 80D limits. This applies even if no insurance premium was paid." },
];

const RELATED = [
  { to: "/itr-filing", label: "ITR Filing", description: "Claim 80D and other deductions correctly", tag: "Service" },
  { to: "/income-tax-calculator", label: "Income Tax Calculator", description: "See your tax savings under Old vs New regime", tag: "Free Tool" },
  { to: "/business-registration", label: "Business Registration", description: "Set up your MSME for group insurance", tag: "Service" },
  { to: "/itr-checklist", label: "ITR Filing Checklist", description: "Includes 80D documents", tag: "Template" },
  { to: "/compliance-roadmap", label: "Business Compliance Roadmap", description: "Compliance check for your MSME", tag: "Tool" },
  { to: "/business-loan-assistance", label: "Business Loan Assistance", description: "Funding for MSMEs in Coimbatore", tag: "Service" },
];

const HealthInsurancePage = () => {
  useCanonical("/health-insurance");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Health Insurance Advisory in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Expert health insurance advisory services for individuals and businesses in Coimbatore."
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  });

  return (
    <>
      <Helmet>
        <title>Health Insurance Advisory in Coimbatore | Expert Guidance | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Expert health insurance advisory in Coimbatore. Individual and group health plans, Section 80D tax benefits. Personalized guidance from WINTRUST SOLUTIONS." />
      </Helmet>
      {/* Hero */}
      <section className="bg-navy py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <nav className="text-slate-400 text-sm mb-4">
              <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">Health Insurance Advisory</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
              Health Insurance Services by IRDAI Registered Agent — Coimbatore
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Protecting your health and the well-being of your employees is paramount. WINTRUST SOLUTIONS provides expert health insurance advisory services in Coimbatore, helping individuals and businesses choose the right coverage at the best rates.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary">Get Insurance Advisory</a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                <MessageCircle size={18} /> WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Types */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Expert Guidance on Individual & Group Health Insurance
          </h2>
          <p className="text-slate-600 mb-8">We help you navigate the complex insurance market to find plans that offer:</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Heart size={32} />, title: "Comprehensive Coverage", desc: "For hospitalization, pre and post-hospitalization expenses, daycare procedures, and more." },
              { icon: <Shield size={32} />, title: "Affordable Premiums", desc: "Plans that fit your budget without compromising on essential benefits and coverage." },
              { icon: <Building size={32} />, title: "Cashless Facility", desc: "Access to a wide network of hospitals in Coimbatore for hassle-free cashless treatment." },
              { icon: <Users size={32} />, title: "Group Plans for MSMEs", desc: "Attract and retain talent with robust group health insurance policies for your employees." },
            ].map((item, index) => (
              <div key={index} className="p-6 bg-slate-50 rounded-lg text-center">
                <div className="text-gold mb-4 flex justify-center">{item.icon}</div>
                <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Health Insurance */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-semibold text-navy mb-6 font-heading">
                Why Health Insurance is Essential
              </h2>
              <ul className="space-y-4">
                {[
                  "Rising Medical Costs - Hospitalization expenses have increased 10-15% annually",
                  "Financial Protection - Avoid depleting savings during medical emergencies",
                  "Tax Benefits - Premiums paid are deductible under Section 80D",
                  "Peace of Mind - Focus on recovery, not on bills",
                  "Cashless Treatment - Get treated without paying upfront",
                  "Coverage for Family - Protect your entire family under one plan",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                    <span className="text-navy">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-navy mb-6">Section 80D Tax Benefits</h3>
              <div className="space-y-4">
                {[
                  { category: "Self, Spouse & Children", amount: "Up to ₹25,000", note: "₹50,000 if senior citizen" },
                  { category: "Parents", amount: "Up to ₹25,000", note: "₹50,000 if senior citizen" },
                  { category: "Maximum Deduction", amount: "Up to ₹1,00,000", note: "If all are senior citizens" },
                ].map((item, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-semibold text-navy">{item.category}</p>
                    <p className="text-gold text-xl font-bold">{item.amount}</p>
                    <p className="text-slate-500 text-sm">{item.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Group Insurance Benefits */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Group Health Insurance for MSMEs
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Employee Retention", desc: "Health insurance is a valuable benefit that helps attract and retain top talent in your organization." },
              { title: "Tax Deductible", desc: "Premiums paid by the company are a tax-deductible business expense, reducing your tax liability." },
              { title: "Improved Productivity", desc: "Healthy employees are more productive. Access to healthcare reduces absenteeism and improves morale." },
            ].map((item, index) => (
              <div key={index} className="p-6 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-navy mb-2 text-xl">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gold py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-navy mb-4 font-heading">
            Get Expert Health Insurance Advice Today
          </h2>
          <p className="text-navy/80 mb-6 max-w-2xl mx-auto">
            Whether you need individual coverage or group insurance for your employees, WINTRUST SOLUTIONS can help you find the perfect plan.
          </p>
          <a href="#contact" className="bg-navy text-white px-8 py-3 rounded-md font-semibold hover:bg-navy-900 transition-colors">
            Get Insurance Advisory
          </a>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection items={FAQS} testId="health-ins-faq" />

      {/* Related Resources */}
      <RelatedResources
        title="Related Tools & Services"
        subtitle="Maximise your tax savings with health insurance"
        items={RELATED}
        testId="health-ins-related"
      />

      {/* IRDAI Disclaimer */}
      <section className="bg-slate-100 py-8" data-testid="irdai-disclaimer-section">
        <div className="container-custom">
          <p className="text-slate-600 text-sm max-w-4xl mx-auto text-center" data-testid="irdai-disclaimer">
            <strong className="text-navy">Disclaimer:</strong> Wintrust Solutions provides health insurance services through a registered IRDAI agent. All insurance products are subject to IRDAI regulations. Please read all policy documents carefully before purchasing.
          </p>
        </div>
      </section>

      <ContactForm />
    </>
  );
};

export default HealthInsurancePage;
